import { useCallback, useEffect, useState } from 'react';
import { AdminSettings, Airstrip, AirstripDistance, Role, ModuleID, RolePermissions, PermissionSet, AgentModule, AgentCustomRule, ScheduledRoute, AircraftType, AgentSettings } from '../types';
import { adminService } from '../services/adminService';
import { ratesService } from '../services/rates';
import { fleetApi } from '../services/fleetApi';
import * as XLSX from 'xlsx';

export type AdminPortalSubTab = 'POLICY' | 'INBOUND_AUTOMATION' | 'SCHEDULED_SOP' | 'AGENT' | 'PERMISSIONS' | 'FLEET' | 'AIRSTRIPS' | 'SERVICES' | 'ROUTES' | 'ROUTE_MATRIX' | 'FUEL' | 'AUDIT' | 'COMMERCIAL_TERMS' | 'RESERVATION_CODES' | 'SPHONE';

type UseAdminPortalArgs = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
  setAirstrips: (a: Airstrip[]) => void;
  setAircrafts: (a: AircraftType[]) => void;
  aircrafts: AircraftType[];
  airstrips: Airstrip[];
};

const DEFAULT_AIRSTRIP_FORM: Partial<Airstrip> = {
  code: '',
  icao: '',
  name: '',
  latitude: 0,
  longitude: 0,
  management: '',
  gpsCode: '',
  runway: '',
  runwayLengthM: 0,
  surfaceType: '',
  elevationFt: 0,
  nmsDar: '',
  brgDar: '',
  status: '',
  timeZoneIndex: '',
  landingFee: 0,
  handlingFee: 0,
  internationalHandlingFee: 0,
  navigationFee: 0,
  safetySecurityDomesticFee: 0,
  safetySecurityInternationalFee: 0,
  paxBusLuggageFee: 0,
  aircraftPermitFee: 0,
  clearanceFee: 0,
  repositionFee: 0,
  paxTax: 0,
  paxTaxInternational: 0,
  arrivalTax: 0,
  fuelAvailable: false,
  fuelPricePerLiter: 0,
  meetAndAssistFee: 0,
  loungeFee: 0,
  country: '',
  isBorderPost: false,
};

type ImportedRouteRow = {
  from: string;
  to: string;
  rackRateUSD: number;
  rackChildUSD?: number;
  basicAdultUSD?: number;
  basicChildUSD?: number;
  taxesUSD?: number;
  notes?: string;
  departureTime?: string;
  arrivalTime?: string;
  daysOfWeek?: number[];
  isActive?: boolean;
  blockTimeMinutes?: number;
  effectiveDate?: string;
};

const isNonNull = <T,>(value: T | null | undefined): value is T => value != null;

const normalizeHeader = (value: unknown) =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[_-]/g, '');

const parseTimeValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const totalMinutes = Math.round((value % 1) * 24 * 60);
    const hh = Math.floor(totalMinutes / 60) % 24;
    const mm = totalMinutes % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  const raw = String(value).trim();
  const direct = raw.match(/^([0-1][0-9]|2[0-3]):([0-5][0-9])$/);
  if (direct) return `${direct[1]}:${direct[2]}`;
  const ampm = raw.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (ampm) {
    let hh = Number(ampm[1]) % 12;
    const mm = Number(ampm[2]);
    if (String(ampm[3]).toLowerCase() === 'pm') hh += 12;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
  return undefined;
};

const parseDaysOfWeek = (value: unknown): number[] | undefined => {
  if (Array.isArray(value)) {
    const parsed = value
      .map(v => Number(v))
      .filter(v => Number.isInteger(v) && v >= 0 && v <= 6);
    return parsed.length ? Array.from(new Set(parsed)).sort((a, b) => a - b) : undefined;
  }
  const raw = String(value || '').trim();
  if (!raw) return undefined;
  const tokens = raw
    .split(/[,\s/;|]+/)
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return undefined;
  const map: Record<string, number> = {
    sun: 0,
    sunday: 0,
    mon: 1,
    monday: 1,
    tue: 2,
    tues: 2,
    tuesday: 2,
    wed: 3,
    wednesday: 3,
    thu: 4,
    thur: 4,
    thurs: 4,
    thursday: 4,
    fri: 5,
    friday: 5,
    sat: 6,
    saturday: 6,
  };
  const parsed = tokens
    .map(token => (map[token] !== undefined ? map[token] : Number(token)))
    .filter(v => Number.isInteger(v) && v >= 0 && v <= 6) as number[];
  return parsed.length ? Array.from(new Set(parsed)).sort((a, b) => a - b) : undefined;
};

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return undefined;
  if (['true', 'yes', 'y', '1', 'active', 'enabled'].includes(raw)) return true;
  if (['false', 'no', 'n', '0', 'inactive', 'disabled'].includes(raw)) return false;
  return undefined;
};

const findHeaderIndex = (rows: unknown[][]): number => {
  for (let i = 0; i < Math.min(rows.length, 25); i += 1) {
    const row = rows[i] || [];
    const headers = row.map(normalizeHeader);
    const hasFrom = headers.some(h => h === 'from' || h === 'origin' || h === 'fromcode');
    const hasTo = headers.some(h => h === 'to' || h === 'destination' || h === 'tocode');
    const hasPrice = headers.some(h =>
      ['rackadult', 'rackrateusd', 'rackrate', 'rate', 'priceusd', 'price', 'totalprice'].includes(h)
    );
    if (hasFrom && hasTo && hasPrice) return i;
  }
  return -1;
};

const resolveImportedLocationToCode = (value: unknown, airstrips: Airstrip[]): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase().replace(/\s+/g, ' ');

  const byCode = airstrips.find(a => String(a.code || '').trim().toUpperCase() === upper);
  if (byCode?.code) return String(byCode.code).toUpperCase();

  const byIcao = airstrips.find(a => String(a.icao || '').trim().toUpperCase() === upper);
  if (byIcao?.code) return String(byIcao.code).toUpperCase();

  const byNameExact = airstrips.find(a => String(a.name || '').trim().toUpperCase() === upper);
  if (byNameExact?.code) return String(byNameExact.code).toUpperCase();

  const byNameContains = airstrips.find(a => String(a.name || '').toUpperCase().includes(upper));
  if (byNameContains?.code) return String(byNameContains.code).toUpperCase();

  // Fallback to raw (backend may still resolve by name in import endpoint).
  return upper;
};

const parseImportedRoutesFile = async (file: File, airstrips: Airstrip[]): Promise<ImportedRouteRow[]> => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  let output: ImportedRouteRow[] = [];

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '' });
    const headerIndex = findHeaderIndex(grid);
    if (headerIndex < 0) continue;

    const headerRow = (grid[headerIndex] || []).map(normalizeHeader);
    const dataRows = grid.slice(headerIndex + 1);
    let lastFrom = '';

    const mapped = dataRows
      .map((arr) => {
        const normalized: Record<string, unknown> = {};
        headerRow.forEach((h, idx) => {
          if (h) normalized[h] = arr[idx];
        });

        const fromRaw = normalized.from || normalized.origin || normalized.fromcode || normalized.departure || '';
        const toRaw = normalized.to || normalized.destination || normalized.tocode || normalized.arrival || '';
        const fromValue = String(fromRaw || '').trim() || lastFrom;
        const toValue = String(toRaw || '').trim();
        if (fromValue) lastFrom = fromValue;
        const from = resolveImportedLocationToCode(fromValue, airstrips);
        const to = resolveImportedLocationToCode(toValue, airstrips);

        const rackRateRaw =
          normalized.rackrateusd ??
          normalized.rackrate ??
          normalized.rackadult ??
          normalized.totalprice ??
          normalized.rate ??
          normalized.priceusd ??
          normalized.price;
        const rackRateUSD = Number(rackRateRaw);
        if (!from || !to || !Number.isFinite(rackRateUSD) || rackRateUSD < 0) return null;
        const rackChildUSD = Number(normalized.rackchild ?? normalized.rackchildusd ?? '');
        const basicAdultUSD = Number(normalized.basicadult ?? normalized.basicadultusd ?? '');
        const basicChildUSD = Number(normalized.basicchild ?? normalized.basicchildusd ?? '');
        const taxesUSD = Number(normalized.taxes ?? normalized.tax ?? '');
        const notes = String(normalized.notes || '').trim() || undefined;

        const departureTime = parseTimeValue(
          normalized.departuretime || normalized.departure || normalized.dept || normalized.etd || normalized.deptime
        );
        const arrivalTime = parseTimeValue(
          normalized.arrivaltime || normalized.arrival || normalized.arrv || normalized.eta || normalized.arrtime
        );
        const daysOfWeek = parseDaysOfWeek(
          normalized.daysofweek || normalized.days || normalized.dow || normalized.operatingdays
        );
        const isActive = parseBoolean(normalized.isactive || normalized.active || normalized.enabled);
        const blockTimeMinutes = Number(
          normalized.blocktimeminutes || normalized.blockminutes || normalized.block || ''
        );
        const effectiveDateRaw = normalized.effectivedate || normalized.dateeffective || '';
        const parsedDate =
          typeof effectiveDateRaw === 'number'
            ? XLSX.SSF.parse_date_code(effectiveDateRaw)
            : null;
        const effectiveDate =
          parsedDate && parsedDate.y && parsedDate.m && parsedDate.d
            ? new Date(Date.UTC(parsedDate.y, parsedDate.m - 1, parsedDate.d)).toISOString()
            : String(effectiveDateRaw || '').trim() || undefined;

        return {
          from,
          to,
          rackRateUSD,
          rackChildUSD: Number.isFinite(rackChildUSD) && rackChildUSD >= 0 ? rackChildUSD : undefined,
          basicAdultUSD: Number.isFinite(basicAdultUSD) && basicAdultUSD >= 0 ? basicAdultUSD : undefined,
          basicChildUSD: Number.isFinite(basicChildUSD) && basicChildUSD >= 0 ? basicChildUSD : undefined,
          taxesUSD: Number.isFinite(taxesUSD) && taxesUSD >= 0 ? taxesUSD : undefined,
          notes,
          departureTime,
          arrivalTime,
          daysOfWeek,
          isActive,
          blockTimeMinutes: Number.isFinite(blockTimeMinutes) && blockTimeMinutes >= 0 ? blockTimeMinutes : undefined,
          effectiveDate,
        } satisfies ImportedRouteRow;
      })
      .filter(isNonNull);

    output = output.concat(mapped);
  }

  // Deduplicate by from+to, keep first occurrence.
  const uniq = new Map<string, ImportedRouteRow>();
  output.forEach((row) => {
    const key = `${row.from}->${row.to}`;
    if (!uniq.has(key)) uniq.set(key, row);
  });
  return Array.from(uniq.values());
};

export const useAdminPortal = ({ settings, setSettings, setAirstrips, setAircrafts, aircrafts, airstrips }: UseAdminPortalArgs) => {
  const [subTab, setSubTab] = useState<AdminPortalSubTab>('POLICY');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [activeAgentModule, setActiveAgentModule] = useState<AgentModule>('QUOTER');
  const [customRulesJsonDraft, setCustomRulesJsonDraft] = useState<string>('[]');
  const [customRulesJsonError, setCustomRulesJsonError] = useState<string | null>(null);
  const [customRuleDraft, setCustomRuleDraft] = useState<AgentCustomRule>({
    id: '',
    title: '',
    severity: 'MEDIUM',
    detail: '',
    suggestion: '',
    enabled: true,
    logic: 'all',
    conditions: [{ field: '', op: 'eq', value: '' }],
  });
  const [ruleTestResult, setRuleTestResult] = useState<string>('');
  const [agentHistory, setAgentHistory] = useState<any[]>([]);

  const fallbackAgent: AgentSettings = {
    enabled: false,
    advisoryOnly: true,
    advisoryMode: 'PANEL' as const,
    provider: {
      type: 'GEMINI' as const,
      apiKey: '',
      baseUrl: '',
      model: 'gemini-1.5-pro',
    },
    modules: {
      QUOTER: true,
      SCHEDULED: true,
      PLANNER: true,
      FUEL: true,
      WHATSAPP: true,
      EMAIL: true,
    },
    rolesByModule: {
      QUOTER: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      SCHEDULED: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      PLANNER: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      FUEL: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      WHATSAPP: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      EMAIL: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
    },
    enforcement: {
      blockSeverities: ['HIGH'],
      allowOverride: true,
      requireOverrideReason: true,
      overrideRoles: ['ADMIN', 'DFO'],
    },
    enforcementByModule: {},
    ruleToggles: {},
    customRulesByModule: {},
    knowledgeBaseNotesByModule: {},
    guidance: {
      includeChecklist: true,
      includePolicyRefs: true,
      knowledgeBaseNotes: '',
    },
    communications: {
      whatsapp: {
        enabled: true,
        draftGuardian: {
          enabled: true,
          mode: 'WARN' as const,
          blockSeverities: ['HIGH'],
          requireProfessionalTone: true,
          suggestRewrite: true,
          preferredPhrases: [
            'Kindly allow us to check and revert to you shortly.',
            'Thank you for your patience.',
          ],
          forbiddenPhrases: ['let me check', 'i dont know', 'not sure'],
        },
        contextAssistant: {
          enabled: true,
          suggestActions: true,
          allowEstimateLookup: true,
          allowQuoteLookup: true,
          allowCustomerHistoryLookup: true,
        },
        intelligence: {
          enabled: true,
          generateSummary: true,
          extractTasks: true,
          extractReferences: true,
        },
        replyWatcher: {
          enabled: true,
          detectMissedReplies: true,
          detectBrokenPromises: true,
          reminderMinutes: 15,
        },
      },
      email: {
        enabled: false,
        draftGuardian: {
          enabled: false,
          mode: 'ASSIST' as const,
          blockSeverities: ['HIGH'],
          requireProfessionalTone: true,
          suggestRewrite: true,
          preferredPhrases: [],
          forbiddenPhrases: [],
        },
        contextAssistant: {
          enabled: false,
          suggestActions: true,
          allowEstimateLookup: true,
          allowQuoteLookup: true,
          allowCustomerHistoryLookup: true,
        },
        intelligence: {
          enabled: false,
          generateSummary: true,
          extractTasks: true,
          extractReferences: true,
        },
        replyWatcher: {
          enabled: false,
          detectMissedReplies: true,
          detectBrokenPromises: true,
          reminderMinutes: 30,
        },
        replyIntelligence: {
          templates: [],
        },
      },
    },
  };

  const agentRoles = ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'] as const;
  const agentSettings = settings.agent || fallbackAgent;
  const setAgentSettings = (next: typeof agentSettings) => {
    const newSettings = { ...settings, agent: next } as AdminSettings;
    setSettings(newSettings);
    setTimeout(() => saveSettings(newSettings), 1000);
  };

  useEffect(() => {
    const rules = agentSettings.customRulesByModule?.[activeAgentModule] || [];
    setCustomRulesJsonDraft(JSON.stringify(rules, null, 2));
    setCustomRulesJsonError(null);
  }, [activeAgentModule, agentSettings.customRulesByModule]);

  const saveSettings = useCallback(async (newSettings: AdminSettings) => {
    setIsSaving(true);
    setSaveStatus('saving');
    try {
      await adminService.saveSettings(newSettings);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('[AdminPortal] Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const loadedSettings = await adminService.getSettings();
        setSettings(loadedSettings);
      } catch (error) {
        console.error('[AdminPortal] Failed to load settings:', error);
      }
    };
    loadSettings();
  }, [setSettings]);

  useEffect(() => {
    const loadHistory = async () => {
      const history = await adminService.getAgentHistory();
      setAgentHistory(history);
    };
    if (subTab === 'AGENT') loadHistory();
  }, [subTab]);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const routesData = await ratesService.getAllRoutes();
        setRoutes(routesData);
      } catch (error) {
        console.error('[AdminPortal] Failed to load routes:', error);
      }
    };
    if (subTab === 'ROUTES') loadRoutes();
  }, [subTab]);

  useEffect(() => {
    const loadDistances = async () => {
      try {
        const distancesData = await ratesService.getAirstripDistances();
        setAirstripDistances(distancesData);
      } catch (error) {
        console.error('[AdminPortal] Failed to load airstrip distances:', error);
      }
    };
    if (subTab === 'ROUTE_MATRIX') loadDistances();
  }, [subTab]);

  const updatePermission = (role: Role, module: ModuleID, type: keyof PermissionSet, value: boolean) => {
    const newPerms = { ...settings.permissions };
    if (!newPerms[role]) {
      newPerms[role] = {} as RolePermissions;
    }
    newPerms[role] = {
      ...newPerms[role],
      [module]: {
        ...newPerms[role][module],
        [type]: value,
      },
    };
    const newSettings = { ...settings, permissions: newPerms };
    setSettings(newSettings);
    setTimeout(() => saveSettings(newSettings), 1000);
  };

  const toggleAllForRole = (role: Role, enabled: boolean) => {
    const newPerms = { ...settings.permissions };
    if (!newPerms[role]) {
      newPerms[role] = {} as RolePermissions;
    }
    modules.forEach(module => {
      newPerms[role] = {
        ...newPerms[role],
        [module]: {
          view: enabled,
          create: enabled,
          edit: enabled,
          delete: enabled,
          approve: enabled,
        },
      };
    });
    const newSettings = { ...settings, permissions: newPerms };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const [showAirstripModal, setShowAirstripModal] = useState(false);
  const [editingAirstrip, setEditingAirstrip] = useState<Airstrip | null>(null);
  const [airstripForm, setAirstripForm] = useState<Partial<Airstrip>>({ ...DEFAULT_AIRSTRIP_FORM });
  const [isSavingAirstrip, setIsSavingAirstrip] = useState(false);
  const [isImportingAirstrips, setIsImportingAirstrips] = useState(false);
  const [airstripImportSummary, setAirstripImportSummary] = useState<{
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [airstripDistances, setAirstripDistances] = useState<AirstripDistance[]>([]);
  const [distanceImportSummary, setDistanceImportSummary] = useState<{
    processed: number;
    skipped: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [isImportingDistances, setIsImportingDistances] = useState(false);
  const [routes, setRoutes] = useState<ScheduledRoute[]>([]);
  const [isImportingRoutes, setIsImportingRoutes] = useState(false);
  const [routeImportSummary, setRouteImportSummary] = useState<{
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [routeMatrixAircraftId, setRouteMatrixAircraftId] = useState('');
  const [isAddingRoute, setIsAddingRoute] = useState(false);
  const [newRoute, setNewRoute] = useState<Omit<ScheduledRoute, 'id'>>({
    from: 'ARK',
    to: 'SEU',
    rackRateUSD: 250,
    departureTime: '08:00',
    arrivalTime: '08:55',
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    isActive: true,
  });

  useEffect(() => {
    if (!routeMatrixAircraftId && aircrafts.length > 0) {
      setRouteMatrixAircraftId(aircrafts[0].id);
    }
  }, [aircrafts, routeMatrixAircraftId]);
  const [isLinkingFuel, setIsLinkingFuel] = useState(false);
  const [fuelLinkNode, setFuelLinkNode] = useState({ code: '', price: 1.45 });
  const [isSyncingFuel, setIsSyncingFuel] = useState(false);
  const [showSyncReview, setShowSyncReview] = useState(false);

  const handleOpenAirstripModal = (airstrip?: Airstrip) => {
    if (airstrip) {
      setEditingAirstrip(airstrip);
      setAirstripForm(airstrip);
    } else {
      setEditingAirstrip(null);
      setAirstripForm({ ...DEFAULT_AIRSTRIP_FORM });
    }
    setShowAirstripModal(true);
  };

  const handleCloseAirstripModal = () => {
    setShowAirstripModal(false);
    setEditingAirstrip(null);
    setAirstripForm({ ...DEFAULT_AIRSTRIP_FORM });
  };

  const handleSaveAirstrip = async () => {
    if (!airstripForm.code || !airstripForm.name || !airstripForm.country) {
      alert('Please fill in all required fields: Code, Name, and Country');
      return;
    }

    setIsSavingAirstrip(true);
    try {
      const userId = localStorage.getItem('ma_userId') || 'system';
      const userName = localStorage.getItem('ma_userName') || 'System User';

      const preparePayload = (form: Partial<Airstrip>) => {
        const payload: any = {};
        if (form.icao !== undefined && String(form.icao || '').trim() !== '') payload.icao = String(form.icao || '').toUpperCase().trim();
        if (form.name !== undefined && form.name !== '') payload.name = form.name;
        if (form.management !== undefined && String(form.management || '').trim() !== '') payload.management = String(form.management || '').trim();
        if (form.gpsCode !== undefined && String(form.gpsCode || '').trim() !== '') payload.gpsCode = String(form.gpsCode || '').trim();
        if (form.runway !== undefined && String(form.runway || '').trim() !== '') payload.runway = String(form.runway || '').trim();
        if (form.runwayLengthM !== undefined && form.runwayLengthM !== null) payload.runwayLengthM = Number(form.runwayLengthM);
        if (form.surfaceType !== undefined && String(form.surfaceType || '').trim() !== '') payload.surfaceType = String(form.surfaceType || '').trim();
        if (form.elevationFt !== undefined && form.elevationFt !== null) payload.elevationFt = Number(form.elevationFt);
        if (form.nmsDar !== undefined && String(form.nmsDar || '').trim() !== '') payload.nmsDar = String(form.nmsDar || '').trim();
        if (form.brgDar !== undefined && String(form.brgDar || '').trim() !== '') payload.brgDar = String(form.brgDar || '').trim();
        if (form.status !== undefined && String(form.status || '').trim() !== '') payload.status = String(form.status || '').trim();
        if (form.timeZoneIndex !== undefined && String(form.timeZoneIndex || '').trim() !== '') payload.timeZoneIndex = String(form.timeZoneIndex || '').trim();
        if (form.latitude !== undefined && form.latitude !== null) payload.latitude = Number(form.latitude);
        if (form.longitude !== undefined && form.longitude !== null) payload.longitude = Number(form.longitude);
        if (form.landingFee !== undefined && form.landingFee !== null) payload.landingFee = Number(form.landingFee);
        if (form.handlingFee !== undefined && form.handlingFee !== null) payload.handlingFee = Number(form.handlingFee);
        if ((form as any).internationalHandlingFee !== undefined && (form as any).internationalHandlingFee !== null) {
          (payload as any).internationalHandlingFee = Number((form as any).internationalHandlingFee);
        }
        if (form.navigationFee !== undefined && form.navigationFee !== null) payload.navigationFee = Number(form.navigationFee);
        if (form.safetySecurityDomesticFee !== undefined && form.safetySecurityDomesticFee !== null) payload.safetySecurityDomesticFee = Number(form.safetySecurityDomesticFee);
        if (form.safetySecurityInternationalFee !== undefined && form.safetySecurityInternationalFee !== null) payload.safetySecurityInternationalFee = Number(form.safetySecurityInternationalFee);
        if (form.paxBusLuggageFee !== undefined && form.paxBusLuggageFee !== null) payload.paxBusLuggageFee = Number(form.paxBusLuggageFee);
        if (form.aircraftPermitFee !== undefined && form.aircraftPermitFee !== null) payload.aircraftPermitFee = Number(form.aircraftPermitFee);
        if (form.clearanceFee !== undefined && form.clearanceFee !== null) payload.clearanceFee = Number(form.clearanceFee);
        if (form.repositionFee !== undefined && form.repositionFee !== null) payload.repositionFee = Number(form.repositionFee);
        if (form.paxTax !== undefined && form.paxTax !== null) payload.paxTax = Number(form.paxTax);
        if (form.paxTaxInternational !== undefined && form.paxTaxInternational !== null) payload.paxTaxInternational = Number(form.paxTaxInternational);
        if (form.arrivalTax !== undefined && form.arrivalTax !== null) payload.arrivalTax = Number(form.arrivalTax);
        if (form.fuelAvailable !== undefined && form.fuelAvailable !== null) payload.fuelAvailable = Boolean(form.fuelAvailable);
        if (form.fuelPricePerLiter !== undefined && form.fuelPricePerLiter !== null) payload.fuelPricePerLiter = Number(form.fuelPricePerLiter);
        if (form.meetAndAssistFee !== undefined && form.meetAndAssistFee !== null) payload.meetAndAssistFee = Number(form.meetAndAssistFee);
        if (form.loungeFee !== undefined && form.loungeFee !== null) payload.loungeFee = Number(form.loungeFee);
        if (form.country !== undefined && form.country !== '') payload.country = form.country;
        if (form.isBorderPost !== undefined && form.isBorderPost !== null) payload.isBorderPost = Boolean(form.isBorderPost);
        if (form.effectiveDate !== undefined) payload.effectiveDate = form.effectiveDate || null;
        return payload;
      };

      if (editingAirstrip) {
        const updatePayload = preparePayload(airstripForm);
        if (Object.keys(updatePayload).length === 0) {
          alert('No changes to update');
          setIsSavingAirstrip(false);
          return;
        }
        await ratesService.updateAirstrip(editingAirstrip.code, updatePayload, userId, userName);
      } else {
        const createPayload = {
          ...preparePayload(airstripForm),
          code: airstripForm.code?.toUpperCase(),
        };
        await ratesService.createAirstrip(createPayload as Omit<Airstrip, 'id' | 'lastUpdated' | 'createdAt' | 'updatedAt'>, userId, userName);
      }

      const updatedAirstrips = await ratesService.getAllAirstrips();
      setAirstrips(updatedAirstrips);
      handleCloseAirstripModal();
    } catch (error: any) {
      console.error('Failed to save airstrip:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const errorDetails = error.response?.data?.errors ? JSON.stringify(error.response.data.errors, null, 2) : '';
      alert(`Failed to save airstrip: ${errorMessage}${errorDetails ? '\n\nDetails:\n' + errorDetails : ''}`);
    } finally {
      setIsSavingAirstrip(false);
    }
  };

  const handleDeleteAirstrip = async (code: string) => {
    if (!confirm(`Are you sure you want to delete airstrip ${code}? This action cannot be undone.`)) {
      return;
    }

    try {
      const userId = localStorage.getItem('ma_userId') || 'system';
      const userName = localStorage.getItem('ma_userName') || 'System User';
      await ratesService.deleteAirstrip(code, userId, userName);

      const updatedAirstrips = await ratesService.getAllAirstrips();
      setAirstrips(updatedAirstrips);
    } catch (error: any) {
      console.error('Failed to delete airstrip:', error);
      alert(`Failed to delete airstrip: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteAllAirstrips = async () => {
    if (!confirm('Delete ALL airstrips? This will remove every record and cannot be undone.')) {
      return;
    }
    if (!confirm('Final confirmation: this will permanently delete all airstrips. Continue?')) {
      return;
    }

    try {
      await ratesService.deleteAllAirstrips();
      const updatedAirstrips = await ratesService.getAllAirstrips();
      setAirstrips(updatedAirstrips);
    } catch (error: any) {
      console.error('Failed to delete all airstrips:', error);
      alert(`Failed to delete all airstrips: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDownloadAirstripTemplate = async () => {
    try {
      const csv = await ratesService.getAirstripImportTemplate();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'airstrips-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(`Failed to download template: ${error.message || 'Unknown error'}`);
    }
  };

  const handleImportAirstripsFile = async (file: File) => {
    if (!file) return;
    setIsImportingAirstrips(true);
    try {
      const csv = await file.text();
      const result = await ratesService.importAirstripsCsv(csv);
      const refreshed = await ratesService.getAllAirstrips();
      setAirstrips(refreshed);
      setAirstripImportSummary(result);
    } catch (error: any) {
      alert(`Failed to import airstrips: ${error.message || 'Unknown error'}`);
    } finally {
      setIsImportingAirstrips(false);
    }
  };

  const handleCloseAirstripImportSummary = () => {
    setAirstripImportSummary(null);
  };

  const handleImportAirstripDistances = async (items: Array<{ fromCode: string; toCode: string; distanceMiles: number }>) => {
    if (!items.length) return;
    setIsImportingDistances(true);
    try {
      const summary = await ratesService.bulkUpsertAirstripDistances(items);
      const refreshed = await ratesService.getAirstripDistances();
      setAirstripDistances(refreshed);
      setDistanceImportSummary(summary);
    } catch (error: any) {
      alert(`Failed to import distances: ${error.message || 'Unknown error'}`);
    } finally {
      setIsImportingDistances(false);
    }
  };

  const handleUpdateAirstripDistance = async (
    fromCode: string,
    toCode: string,
    payload: {
      distanceMiles?: number;
      overrideSpeedKts?: number;
      overrideRatePerMileUSD?: number;
      overrideRatePerHourUSD?: number;
      overrideBlockMinutes?: number;
    }
  ) => {
    try {
      const updated = await ratesService.upsertAirstripDistance(fromCode, toCode, payload);
      setAirstripDistances(prev => {
        const next = prev.filter(item => !(item.fromCode === updated.fromCode && item.toCode === updated.toCode));
        return [...next, updated].sort((a, b) => a.fromCode.localeCompare(b.fromCode) || a.toCode.localeCompare(b.toCode));
      });
    } catch (error: any) {
      alert(`Failed to update distance: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteAirstripDistance = async (fromCode: string, toCode: string) => {
    try {
      await ratesService.deleteAirstripDistance(fromCode, toCode);
      setAirstripDistances(prev => prev.filter(item => !(item.fromCode === fromCode && item.toCode === toCode)));
    } catch (error: any) {
      alert(`Failed to delete distance: ${error.message || 'Unknown error'}`);
    }
  };

  const handleOpenAddRoute = () => setIsAddingRoute(true);
  const handleCloseAddRoute = () => setIsAddingRoute(false);

  const handleUpdateNewRoute = (updates: Partial<Omit<ScheduledRoute, 'id'>>) => {
    setNewRoute(prev => ({ ...prev, ...updates }));
  };

  const handleToggleRouteDay = (day: number) => {
    setNewRoute(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day].sort(),
    }));
  };

  const handleCreateRoute = async () => {
    if (newRoute.from === newRoute.to) {
      alert('Origin and Destination cannot be the same.');
      return;
    }
    try {
      await ratesService.createRoute(newRoute, 'ADMIN-USER');
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
      setIsAddingRoute(false);
    } catch (error: any) {
      alert(`Failed to create route: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteRoute = async (id: string) => {
    if (!confirm('Are you sure you want to decommission this commercial route? Historical quotes will remain but no new bookings can be made.')) {
      return;
    }
    try {
      await ratesService.deleteRoute(id, 'ADMIN-USER');
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
    } catch (error: any) {
      alert(`Failed to delete route: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteAllRoutes = async () => {
    if (!confirm('Delete ALL routes? This will remove the full route registry and cannot be undone.')) {
      return;
    }
    if (!confirm('Final confirmation: permanently delete all scheduled routes?')) {
      return;
    }
    try {
      await ratesService.deleteAllRoutes();
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
      setRouteImportSummary(null);
    } catch (error: any) {
      alert(`Failed to delete all routes: ${error.message || 'Unknown error'}`);
    }
  };

  const handleUpdateRouteRate = async (id: string, rate: number) => {
    try {
      await ratesService.updateRoute(id, { rackRateUSD: rate });
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
    } catch (error) {
      console.error('Failed to update route rate:', error);
    }
  };

  const handleUpdateRouteFields = async (id: string, updates: Partial<ScheduledRoute>) => {
    try {
      await ratesService.updateRoute(id, updates);
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
    } catch (error) {
      console.error('Failed to update route fields:', error);
    }
  };

  const handleImportRoutesFile = async (file: File) => {
    if (!file) return;
    setIsImportingRoutes(true);
    try {
      const rows = await parseImportedRoutesFile(file, airstrips);
      if (!rows.length) {
        alert('No valid route rows found in the uploaded file.');
        return;
      }
      const summary = await ratesService.importRoutes(rows);
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
      setRouteImportSummary(summary);
    } catch (error: any) {
      alert(`Failed to import routes: ${error.message || 'Unknown error'}`);
    } finally {
      setIsImportingRoutes(false);
    }
  };

  const handleCloseRouteImportSummary = () => {
    setRouteImportSummary(null);
  };

  const handleSelectRouteMatrixAircraft = (id: string) => {
    setRouteMatrixAircraftId(id);
  };

  const handleUpdateRouteDefaults = (updates: Partial<AdminSettings['routeDefaults']>) => {
    const defaults = settings.routeDefaults || {
      cruiseSpeedKts: 120,
      ratePerNmUSD: 0,
      ratePerHourUSD: 0,
      distanceComputationModel: 'GEODESIC',
      distanceBufferPercent: 0,
      distanceBaseMiles: 0,
      roundDistanceUp: false,
      blockTimeMultiplier: 1,
      blockTimeBaseMinutes: 0,
      blockTimeRoundingMinutes: 1,
    };
    const next = {
      ...settings,
      routeDefaults: {
        ...defaults,
        ...updates,
      },
    };
    setSettings(next);
    setTimeout(() => saveSettings(next), 800);
  };

  const handleUpdateAircraftRoutePricing = async (aircraftId: string, updates: Partial<AircraftType>) => {
    try {
      const updated = await fleetApi.updateAircraft(aircraftId, updates);
      setTimeout(async () => {
        try {
          const refreshed = await fleetApi.getAircrafts();
          setAircrafts(Array.isArray(refreshed) ? refreshed : []);
          if (!routeMatrixAircraftId && refreshed.length > 0) {
            setRouteMatrixAircraftId(refreshed[0].id);
          }
        } catch {
          // ignore refresh errors
        }
      }, 300);
      return updated;
    } catch (error) {
      console.error('Failed to update aircraft route pricing:', error);
      alert('Failed to update aircraft route pricing');
      return null;
    }
  };

  const handleUpsertRouteAircraftRate = async (
    routeId: string,
    aircraftId: string,
    ratePerNmUSD: number,
    ratePerHourUSD: number
  ) => {
    try {
      await ratesService.upsertRouteAircraftRate(routeId, {
        aircraftId,
        ratePerNmUSD,
        ratePerHourUSD,
      });
      const routesData = await ratesService.getAllRoutes();
      setRoutes(routesData);
    } catch (error: any) {
      alert(`Failed to update route aircraft rate: ${error.message || 'Unknown error'}`);
    }
  };

  const handleOpenLinkFuel = () => setIsLinkingFuel(true);
  const handleCloseLinkFuel = () => setIsLinkingFuel(false);

  const handleUpdateFuelLinkNode = (updates: Partial<{ code: string; price: number }>) => {
    setFuelLinkNode(prev => ({ ...prev, ...updates }));
  };

  const handleLinkFuelNode = async () => {
    if (!fuelLinkNode.code) return;
    try {
      await ratesService.updateAirstrip(
        fuelLinkNode.code,
        {
          fuelAvailable: true,
          fuelPricePerLiter: fuelLinkNode.price,
        },
        'ADMIN-USER'
      );
      const airstripsData = await ratesService.getAllAirstrips();
      setAirstrips(airstripsData);
      setIsLinkingFuel(false);
    } catch (error: any) {
      alert(`Failed to link fuel node: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDecommissionFuelNode = async (code: string) => {
    if (!confirm(`Are you sure you want to decommission the fuel supply node at ${code}? This station will no longer be available for fleet refuelling missions.`)) {
      return;
    }
    try {
      await ratesService.updateAirstrip(code, { fuelAvailable: false }, 'ADMIN-USER');
      const airstripsData = await ratesService.getAllAirstrips();
      setAirstrips(airstripsData);
    } catch (error: any) {
      alert(`Failed to decommission fuel node: ${error.message || 'Unknown error'}`);
    }
  };

  const handleUpdateFuelPriceInline = async (code: string, price: number) => {
    try {
      await ratesService.updateAirstrip(code, { fuelPricePerLiter: price }, 'ADMIN-USER');
      const airstripsData = await ratesService.getAllAirstrips();
      setAirstrips(airstripsData);
    } catch (error) {
      console.error('Failed to update fuel price:', error);
    }
  };

  const handleTriggerFuelSync = () => {
    setIsSyncingFuel(true);
    setTimeout(() => {
      setIsSyncingFuel(false);
      setShowSyncReview(true);
    }, 1200);
  };

  const handleCloseSyncReview = () => setShowSyncReview(false);

  const handleCommitFuelSync = async () => {
    try {
      const stations = await ratesService.getAllAirstrips();
      await Promise.all(
        stations
          .filter(as => as.fuelAvailable)
          .map(as => {
            const adjustment = Math.random() * 0.1 - 0.05;
            return ratesService.updateAirstrip(
              as.code,
              { fuelPricePerLiter: (as.fuelPricePerLiter || 1.45) + adjustment },
              'SYSTEM-SYNC'
            );
          })
      );
      const airstripsData = await ratesService.getAllAirstrips();
      setAirstrips(airstripsData);
      setShowSyncReview(false);
    } catch (error: any) {
      alert(`Failed to sync fuel prices: ${error.message || 'Unknown error'}`);
    }
  };

  const rollbackAgentSnapshot = async (snapshotId: string) => {
    const ok = confirm('Rollback agent settings to this snapshot?');
    if (!ok) return;
    const updated = await adminService.rollbackAgent(snapshotId);
    if (updated) {
      setSettings(updated);
    }
  };

  const roles: Role[] = ['ADMIN', 'CEO', 'GROUND_OPS', 'RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS'];
  const modules: ModuleID[] = ['DASHBOARD', 'OPERATIONS_TV', 'QUOTER', 'PLANNER', 'FLEET', 'CUSTOMERS', 'FINANCE', 'ADMIN', 'OUTBOUND', 'USERS', 'SCHEDULED', 'AUDIT'];

  return {
    subTab,
    setSubTab,
    isSaving,
    saveStatus,
    saveSettings,
    activeAgentModule,
    setActiveAgentModule,
    customRulesJsonDraft,
    setCustomRulesJsonDraft,
    customRulesJsonError,
    setCustomRulesJsonError,
    customRuleDraft,
    setCustomRuleDraft,
    ruleTestResult,
    setRuleTestResult,
    agentHistory,
    agentRoles,
    agentSettings,
    setAgentSettings,
    updatePermission,
    toggleAllForRole,
    roles,
    modules,
    showAirstripModal,
    editingAirstrip,
    airstripForm,
    setAirstripForm,
    isSavingAirstrip,
    isImportingAirstrips,
    airstripImportSummary,
    airstripDistances,
    distanceImportSummary,
    isImportingDistances,
    handleOpenAirstripModal,
    handleCloseAirstripModal,
    handleSaveAirstrip,
    handleDeleteAirstrip,
    handleDeleteAllAirstrips,
    handleDownloadAirstripTemplate,
    handleImportAirstripsFile,
    handleCloseAirstripImportSummary,
    handleImportAirstripDistances,
    handleUpdateAirstripDistance,
    handleDeleteAirstripDistance,
    routes,
    isImportingRoutes,
    routeImportSummary,
    isAddingRoute,
    newRoute,
    handleOpenAddRoute,
    handleCloseAddRoute,
    handleUpdateNewRoute,
    handleToggleRouteDay,
    handleCreateRoute,
    handleDeleteRoute,
    handleDeleteAllRoutes,
    handleUpdateRouteRate,
    handleUpdateRouteFields,
    handleImportRoutesFile,
    handleCloseRouteImportSummary,
    handleUpsertRouteAircraftRate,
    routeMatrixAircraftId,
    handleSelectRouteMatrixAircraft,
    handleUpdateRouteDefaults,
    handleUpdateAircraftRoutePricing,
    isLinkingFuel,
    fuelLinkNode,
    isSyncingFuel,
    showSyncReview,
    handleOpenLinkFuel,
    handleCloseLinkFuel,
    handleUpdateFuelLinkNode,
    handleLinkFuelNode,
    handleDecommissionFuelNode,
    handleUpdateFuelPriceInline,
    handleTriggerFuelSync,
    handleCloseSyncReview,
    handleCommitFuelSync,
    rollbackAgentSnapshot,
  };
};
